import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /** Context hint shown in the subtitle, e.g. "Reddit" or "X" */
  triggeredBy?: string;
}

const PLANS = [
  {
    id: 'personal',
    name: 'Personal',
    price: '$9',
    period: '/mo',
    badge: null,
    highlight: true,
    features: [
      'Reddit + X + Bluesky + YouTube',
      'Up to 20 Listening Rules',
      'Up to 20 Fences',
      'RSS / Newsletter feeds',
      'AI content filtering',
    ],
  },
  {
    id: 'family',
    name: 'Family',
    price: '$15',
    period: '/mo',
    badge: null,
    highlight: false,
    features: [
      'Everything in Personal',
      'All platforms (incl. future)',
      'Unlimited rules + fences',
      'Parent Lock + child profiles',
      'Priority email support',
    ],
  },
] as const;

const FOUNDING = {
  id: 'founding',
  name: 'Founding Member',
  price: '$49',
  period: '/yr',
  spotsLeft: 500,
  description: 'One-time annual plan · Personal-tier access · Founding badge · 500 spots only',
};

export function UpgradeModal({ open, onClose, triggeredBy }: UpgradeModalProps) {
  const [loading, setLoading] = useState<string | null>(null);

  const startCheckout = async (tier: string) => {
    setLoading(tier);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in first');
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier },
      });
      if (error || !data?.url) {
        toast.error('Could not start checkout — please try again');
        return;
      }
      window.location.href = data.url;
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const subtitle = triggeredBy
    ? `${triggeredBy} is included in paid plans. Upgrade to connect it now.`
    : 'Upgrade to unlock Reddit, X, and more.';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Unlock more platforms</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-xl border p-4 flex flex-col gap-3 ${
                plan.highlight
                  ? 'border-[#1A2E4A] bg-[#1A2E4A]/[0.03]'
                  : 'border-border'
              }`}
            >
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">{plan.price}</span>
                  <span className="text-sm text-muted-foreground">{plan.period}</span>
                </div>
                <p className="font-semibold mt-0.5">{plan.name}</p>
              </div>

              <ul className="space-y-1.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#1A2E4A]" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                className={
                  plan.highlight
                    ? 'w-full bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white'
                    : 'w-full'
                }
                variant={plan.highlight ? 'default' : 'outline'}
                onClick={() => startCheckout(plan.id)}
                disabled={loading !== null}
              >
                {loading === plan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  `Get ${plan.name}`
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Founding member option */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3 mt-1">
          <div className="flex items-center gap-2 min-w-0">
            <Star className="h-4 w-4 text-amber-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">{FOUNDING.name} — {FOUNDING.price}{FOUNDING.period}</p>
              <p className="text-xs text-amber-700 truncate">{FOUNDING.description}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
            onClick={() => startCheckout(FOUNDING.id)}
            disabled={loading !== null}
          >
            {loading === FOUNDING.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Join'}
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-1">
          Cancel anytime · Secure checkout via Stripe
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
